import { useState, useEffect, useCallback } from 'react';
import { exitApi } from '../services/api';
import { formatCurrency, formatDateTime, formatApiError } from '../utils/helpers';
import { MagnifyingGlass, ArrowCounterClockwise, Warning, CheckCircle } from '@phosphor-icons/react';

// ============================================================
// ADMIN CORRECTIONS PANEL
// Search recent (not-voided) checkouts and restore them in case
// of an accidental closeout. Requires a reason for each correction
// and writes a full audit log entry. Excludes voided transactions
// from revenue & reports.
// ============================================================

const PAYMENT_LABEL = {
  cash: 'Efectivo',
  card_transfer: 'Tarjeta/Transf.',
};

export default function AdminCorrections() {
  const [query, setQuery] = useState('');
  const [hours, setHours] = useState(24);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmTx, setConfirmTx] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const search = useCallback(async () => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await exitApi.search(query.trim() || null, hours);
      setResults(res.data || []);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [query, hours]);

  useEffect(() => { search(); }, [hours]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitCorrection = async () => {
    if (!confirmTx) return;
    if (!reason.trim()) { setError('Debe indicar una razón'); return; }
    setSubmitting(true); setError('');
    try {
      const res = await exitApi.correct(confirmTx.transaction_id, reason.trim());
      setSuccess(res.data.message || 'Corrección aplicada');
      setConfirmTx(null);
      setReason('');
      await search();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-corrections-panel">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1C1C1E] to-[#141414] border border-[#27272A] rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-[#D97706]/10 rounded-lg">
            <ArrowCounterClockwise className="w-5 h-5 text-[#FCD34D]" weight="bold" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Correcciones de salida</h2>
            <p className="text-[#A1A1AA] text-xs">Restaurar vehículos cerrados por error. La transacción incorrecta se anula (no se borra).</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlass className="w-4 h-4 text-[#71717A] absolute left-3 top-1/2 -translate-y-1/2" weight="bold" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              placeholder="Buscar por patente, espacio (ej. A2) o ticket ID..."
              className="w-full pl-9 pr-3 py-2 bg-[#1C1C1E] border border-[#27272A] rounded-lg text-white text-sm focus:border-[#16A34A] outline-none"
              data-testid="corrections-search-input"
            />
          </div>
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="px-3 py-2 bg-[#1C1C1E] border border-[#27272A] rounded-lg text-white text-sm outline-none focus:border-[#16A34A]"
            data-testid="corrections-hours-select"
          >
            <option value={1}>Última hora</option>
            <option value={6}>Últimas 6 horas</option>
            <option value={24}>Últimas 24 horas</option>
            <option value={168}>Última semana</option>
            <option value={720}>Último mes</option>
          </select>
          <button
            onClick={search}
            disabled={loading}
            className="px-4 py-2 bg-[#27272A] hover:bg-[#3F3F46] disabled:opacity-50 rounded-lg text-white text-sm font-medium"
            data-testid="corrections-search-btn"
          >
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>

        {error && (
          <div className="p-3 bg-[#450A0A] border border-[#DC2626] text-[#FCA5A5] rounded-lg text-sm flex items-center gap-2" data-testid="corrections-error">
            <Warning className="w-4 h-4" weight="bold" /> {error}
          </div>
        )}
        {success && (
          <div className="p-3 bg-[#052E16] border border-[#16A34A] text-[#4ADE80] rounded-lg text-sm flex items-center gap-2" data-testid="corrections-success">
            <CheckCircle className="w-4 h-4" weight="bold" /> {success}
          </div>
        )}
      </div>

      {/* Results table */}
      <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-4">
        <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-3">
          Salidas recientes ({results.length})
        </h3>
        {results.length === 0 ? (
          <p className="text-[#71717A] text-sm text-center py-6">
            {loading ? 'Cargando...' : 'Sin resultados'}
          </p>
        ) : (
          <div className="overflow-x-auto" data-testid="corrections-results-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#A1A1AA] text-xs uppercase tracking-wide border-b border-[#27272A]">
                  <th className="py-2 pr-3">Fecha/Hora</th>
                  <th className="py-2 pr-3">Espacio</th>
                  <th className="py-2 pr-3">Patente</th>
                  <th className="py-2 pr-3">Tipo</th>
                  <th className="py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3">Pago</th>
                  <th className="py-2 pr-3">Operador</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {results.map((tx) => (
                  <tr key={tx.transaction_id} className="border-b border-[#27272A]/50 hover:bg-[#1C1C1E]" data-testid={`correction-row-${tx.space_label}`}>
                    <td className="py-2 pr-3 text-[#A1A1AA] font-mono text-xs">{formatDateTime(tx.created_at)}</td>
                    <td className="py-2 pr-3 text-white font-mono font-bold">{tx.space_label}</td>
                    <td className="py-2 pr-3 text-white font-mono">{tx.plate_number}</td>
                    <td className="py-2 pr-3 text-[#A1A1AA]">{tx.vehicle_type}</td>
                    <td className="py-2 pr-3 text-white font-mono text-right">{formatCurrency(tx.total_price)}</td>
                    <td className="py-2 pr-3 text-[#A1A1AA]">
                      {PAYMENT_LABEL[tx.payment_method] || <span className="italic">No esp.</span>}
                    </td>
                    <td className="py-2 pr-3 text-[#A1A1AA] text-xs">{tx.check_out_operator}</td>
                    <td className="py-2">
                      <button
                        onClick={() => { setConfirmTx(tx); setReason(''); setError(''); }}
                        className="px-3 py-1 bg-[#D97706] hover:bg-[#F59E0B] text-white text-xs font-bold rounded"
                        data-testid={`correction-restore-${tx.space_label}`}
                      >
                        Restaurar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirm dialog */}
      {confirmTx && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" data-testid="correction-confirm-modal">
          <div className="bg-[#141414] border border-[#D97706] rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <Warning className="w-7 h-7 text-[#FCD34D]" weight="bold" />
              <h3 className="text-white font-bold text-lg">Confirmar corrección</h3>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-3">
              Se restaurará el vehículo <strong className="text-white">{confirmTx.plate_number}</strong> en el espacio <strong className="text-white">{confirmTx.space_label}</strong> y se anulará el cobro de <strong className="text-[#FCD34D]">{formatCurrency(confirmTx.total_price)}</strong>.
            </p>
            <label className="text-[#A1A1AA] text-xs uppercase tracking-wide block mb-1">Razón (obligatoria)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Ej: Cierre erróneo del espacio A3, debía cerrarse A2"
              className="w-full p-2 bg-[#1C1C1E] border border-[#27272A] rounded-lg text-white text-sm mb-3 focus:border-[#16A34A] outline-none"
              data-testid="correction-reason-input"
            />
            {error && (
              <div className="mb-3 p-2 bg-[#450A0A] border border-[#DC2626] text-[#FCA5A5] rounded text-xs">{error}</div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => { setConfirmTx(null); setReason(''); setError(''); }}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-[#27272A] hover:bg-[#3F3F46] rounded-lg text-white text-sm"
                data-testid="correction-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={submitCorrection}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-[#D97706] hover:bg-[#F59E0B] disabled:opacity-50 rounded-lg text-white text-sm font-bold"
                data-testid="correction-confirm-btn"
              >
                {submitting ? 'Procesando...' : 'Restaurar y anular cobro'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
